import React, { useCallback, useEffect, useRef, useState } from "react";
import { debounce } from "../../utils/utilDebounceInput";

const TextField = ({
  title = "",
  type = "text",
  value = "",
  required = false,
  lines = 1,
  baseLine = 0,
  placeholder,
  onChange,
  onSubmit = () => {},
  errorMessage = "",
  className = "",
  debounceOnChangeValidation = null,
  inputRegex = null, // TODO: show tooltip when regex is not matched. until the next change.
  regexMessage = "",
  disabled = false,
  maxCharacters = -1,
  onClick = () => {},
  onFocus = () => {},
  prefixIcon = null,
}) => {
  const [regexError, setRegexError] = useState("");
  const timeoutRef = useRef(null);
  const textAreaRef = useRef(null);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      onSubmit();
    }
  };

  const debounceChange = useCallback(
    debounce((value) => {
      debounceOnChangeValidation(value);
    }, 500),
    []
  );

  const internalOnChange = (value) => {
    if (inputRegex != null && !new RegExp(inputRegex).test(value)) {
      setRegexError(regexMessage);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setRegexError("");
      }, 2000);
      return;
    }
    setRegexError("");
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    onChange(value);
    if (lines > 1) {
      adjustHeight(value);
    }

    if (debounceOnChangeValidation == null) return;
    debounceChange(value);
  };

  var textareaWidth = textAreaRef?.current?.clientWidth;

  const adjustHeight = (value) => {
    if (!textAreaRef && !textAreaRef.current && textAreaRef.current === null) return;
    textAreaRef.current.style.height = "auto";

    const computedStyle = window.getComputedStyle(textAreaRef.current);
    const lineHeight = 24;

    if (!textareaWidth) {
      textareaWidth = textAreaRef?.current?.clientWidth;
    }
    const textareaPadding =
      parseInt(computedStyle.paddingTop, 10) + parseInt(computedStyle.paddingBottom, 10);
    const lettersPerLine = Math.floor(textareaWidth / 8);
    const lines = Math.ceil((value?.length + 1) / lettersPerLine);
    if (lines === 1 && baseLine === 1) {
      textAreaRef.current.style.height = `${Math.max(40, lineHeight + textareaPadding)}px`;
      return;
    }

    // Calculate the number of lines
    var newHeight = Math.max(lines * lineHeight, 10 + textAreaRef.current.scrollHeight);
    newHeight = Math.min(400, newHeight);

    // Set the new height
    textAreaRef.current.style.height = `${newHeight}px`;
  };

  const handleFocus = () => {
    const viewportMetaTag = document.querySelector('meta[name="viewport"]');
    if (viewportMetaTag) {
      viewportMetaTag.content =
        "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no";
    }
  };

  const handleBlur = () => {
    const viewportMetaTag = document.querySelector('meta[name="viewport"]');
    if (viewportMetaTag) {
      viewportMetaTag.content = "width=device-width, initial-scale=1.0";
    }
  };

  useEffect(() => {
    if (lines > 1) {
      adjustHeight(value);
    }
  }, [value]);

  // handle window resize to adjust the height of the textarea
  useEffect(() => {
    const handleResize = () => {
      if (lines > 1) {
        adjustHeight(value);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [value]);

  return (
    <div
      className={`w-full relative transition-all text-[12px] md:text-[14px] xl:text-[16px] ${className}`}
    >
      {(type === "time" || (value && value.length > 0)) && title && (
        <p
          className={`text-[12px] text-ov-gray-700 absolute left-1  z-20 transition-all rounded-lg top-0 -translate-y-1/2 ${
            disabled ? "bg-ov-disabled-input" : "bg-white"
          } px-2
          }`}
        >
          {title}
        </p>
      )}
      {prefixIcon && (
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
          <img src={prefixIcon} alt="prefix icon" className="size-4" />
        </div>
      )}
      {required && <p className="text-red-500 absolute right-1 top-[3px]">*</p>}
      {maxCharacters > 0 && (
        <p
          className={`text-gray-500 absolute text-[12px] right-2 ${
            lines === "single" ? "bottom-1" : "bottom-2"
          }`}
        >
          {value?.trim().length}/{maxCharacters}
        </p>
      )}
      {lines > 1 && (
        <textarea
          ref={textAreaRef}
          disabled={disabled}
          value={value}
          placeholder={placeholder}
          onChange={(e) => internalOnChange(e.target.value)}
          className={`w-full ${maxCharacters > 0 ? "pr-14" : ""} ${
            prefixIcon ? "pl-8 md:pl-10" : ""
          } resize-none z-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-gray-500 disabled:bg-ov-disabled-input ${
            errorMessage
              ? "border-b border-b-red-500 focus:border-b-red-500"
              : value?.length > 0
              ? "border-b border-b-ov-green focus:border-b-ov-green"
              : ""
          } `}
          onKeyDown={handleKeyPress}
          maxLength={maxCharacters}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      )}
      {lines === 1 && (
        <input
          onClick={onClick}
          onFocus={onFocus}
          disabled={disabled}
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => internalOnChange(e.target.value)}
          className={`w-full ${maxCharacters > 0 ? "pr-14" : ""} ${
            prefixIcon ? "pl-8 md:pl-10" : ""
          } text-[12px] md:text-[14px] h-10 z-10 px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:border-gray-500 disabled:bg-ov-disabled-input ${
            errorMessage
              ? "border-b border-b-red-500 focus:border-b-red-500"
              : value.length > 0
              ? "border-b border-b-ov-green focus:border-b-ov-green"
              : ""
          } ${className}`}
          onKeyDown={handleKeyPress}
          maxLength={maxCharacters}
        />
      )}
      {errorMessage && !regexError && (
        <p
          className={`absolute ${
            lines === 1 ? "" : "-bottom-2 sm:-bottom-3"
          } text-[8px] sm:text-[12px] text-red-500`}
        >
          {errorMessage}
        </p>
      )}
      {regexError && (
        <p
          className={`absolute ${
            lines === 1 ? "" : "-bottom-2 sm:-bottom-3"
          } text-[8px] sm:text-[12px] text-red-500`}
        >
          {regexError}
        </p>
      )}
    </div>
  );
};

export default TextField;
